import React, { useState } from 'react';
import { Modal } from '@/components/primitives/Modal';
import { Button } from '@/components/primitives/Button';
import { Icon } from '@/components/primitives/Icon';
import { createCampaign } from '@/services/campaigns';
import type { Campaign } from '@/types/api';

interface NewCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCampaignCreated?: (campaign: Campaign) => void;
}

export function NewCampaignModal({ isOpen, onClose, onCampaignCreated }: NewCampaignModalProps) {
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('brand');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!name.trim()) {
      setError('Campaign name is required.');
      return;
    }
    setError('');
    setIsSubmitting(true);

    try {
      // Goal, StartDate, and EndDate are mocked here relative to standard API
      const campaign = await createCampaign({
        name: name.trim(),
      });

      if (onCampaignCreated) onCampaignCreated(campaign);

      // Complete & reset
      setName('');
      setGoal('brand');
      setStartDate('');
      setEndDate('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New Campaign"
      subtitle="Configure your basic campaign settings below."
      footer={
        <Button
          onClick={handleSave}
          disabled={isSubmitting || !name.trim()}
          className="w-full py-4 text-base shadow-lg shadow-primary/20 justify-center"
        >
          {isSubmitting ? 'Saving...' : 'Save Campaign'}
        </Button>
      }
    >
      <form id="new-campaign-form" onSubmit={handleSave} className="space-y-8">
        {error && (
          <div className="p-3 rounded-lg bg-error-container text-on-error-container text-sm">
            {error}
          </div>
        )}
        <div className="space-y-6">
          <div className="space-y-2">
            <label
              className="text-[11px] uppercase font-black tracking-widest text-on-surface-variant block"
              htmlFor="name"
            >
              Campaign Name
            </label>
            <input
              className="w-full bg-surface-container-low border border-outline-variant/20 rounded-xl px-4 py-4 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-on-surface-variant/40 placeholder:font-normal"
              id="name"
              placeholder="Summer Collection 2024"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <label
              className="text-[11px] uppercase font-black tracking-widest text-on-surface-variant block"
              htmlFor="goal"
            >
              Campaign Goal
            </label>
            <div className="relative">
              <select
                className="w-full bg-surface-container-low border border-outline-variant/20 rounded-xl pl-4 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all appearance-none text-on-surface font-medium cursor-pointer disabled:cursor-not-allowed"
                id="goal"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                disabled={isSubmitting}
              >
                <option value="brand">Brand Awareness</option>
                <option value="traffic">Website Traffic</option>
                <option value="leads">Lead Generation</option>
                <option value="sales">Sales Conversion</option>
              </select>
              <Icon
                name="expand_more"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none"
              />
            </div>
          </div>
        </div>
        <div className="pt-6 border-t border-outline-variant/10 space-y-4">
          <h3 className="text-[11px] uppercase font-black tracking-widest text-primary">
            Campaign Dates
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label
                className="text-[10px] uppercase font-black tracking-widest text-on-surface-variant block"
                htmlFor="start-date"
              >
                Launch Date
              </label>
              <div className="relative">
                <Icon
                  name="calendar_today"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant/60 text-sm pointer-events-none"
                />
                <input
                  className="w-full bg-surface-container-low border border-outline-variant/20 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-on-surface"
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label
                className="text-[10px] uppercase font-black tracking-widest text-on-surface-variant block"
                htmlFor="end-date"
              >
                Close Date
              </label>
              <div className="relative">
                <Icon
                  name="event_busy"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant/60 text-sm pointer-events-none"
                />
                <input
                  className="w-full bg-surface-container-low border border-outline-variant/20 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-on-surface"
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
}
